import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

const HERO = "https://images.pexels.com/photos/33703700/pexels-photo-33703700.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Demo panel is hidden by default. Set REACT_APP_SHOW_DEMO=true in the frontend
// build env to show it (e.g. for staging/preview environments only).
const SHOW_DEMO = String(process.env.REACT_APP_SHOW_DEMO || "").toLowerCase() === "true";

const DEMO = [
  { role: "Administrator", email: "admin@kabirinfra.com", password: "Admin@123" },
  { role: "Indent Creator", email: "indent@kabirinfra.com", password: "Password@123" },
  { role: "QS Engineer", email: "qs@kabirinfra.com", password: "Password@123" },
  { role: "Purchase Executive", email: "purchase@kabirinfra.com", password: "Password@123" },
  { role: "Purchase Manager", email: "purchasemgr@kabirinfra.com", password: "Password@123" },
  { role: "Store Keeper", email: "store@kabirinfra.com", password: "Password@123" },
  { role: "Accounts Executive", email: "accounts@kabirinfra.com", password: "Password@123" },
  { role: "Accounts Manager", email: "accountsmgr@kabirinfra.com", password: "Password@123" },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(SHOW_DEMO ? "admin@kabirinfra.com" : "");
  const [password, setPassword] = useState(SHOW_DEMO ? "Admin@123" : "");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left */}
      <div className="flex flex-col justify-center px-6 lg:px-16 py-10 max-w-2xl w-full mx-auto">
        <div className="mb-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-kabir grid place-items-center text-zinc-900 font-bold text-xl font-display">K</div>
          <div>
            <div className="font-display font-bold text-xl leading-tight">KABIR<span className="text-kabir"> INFRA</span></div>
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Procurement ERP</div>
          </div>
        </div>

        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Sign in
        </h1>
        <p className="text-muted-foreground mb-8 text-sm">
          Enterprise procurement, from indent to payment — all in one place.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide">Email</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@kabirinfra.com" required
              data-testid="login-email" className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide">Password</Label>
            <Input
              id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" required
              data-testid="login-password" className="mt-1.5 h-11"
            />
          </div>
          <Button
            type="submit" disabled={loading}
            data-testid="login-submit"
            className="w-full h-11 bg-kabir text-zinc-900 hover:bg-[#DCA000] font-semibold active:scale-[0.98] transition-all"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            Sign in →
          </Button>
        </form>

        {SHOW_DEMO && (
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-3 flex items-center gap-2">
            <ShieldCheck size={12} /> Demo Accounts
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {DEMO.map((d) => (
              <button
                key={d.email}
                type="button"
                onClick={() => { setEmail(d.email); setPassword(d.password); }}
                data-testid={`demo-${d.role.replace(/\s/g, "-").toLowerCase()}`}
                className="text-left px-3 py-2 border border-border rounded-md hover:border-kabir hover:bg-accent/50 transition-all"
              >
                <div className="font-semibold text-foreground">{d.role}</div>
                <div className="text-muted-foreground font-mono text-[10px] truncate">{d.email}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 text-[10px] text-muted-foreground">
            Password for all demo accounts: <span className="font-mono">Password@123</span> (admin: <span className="font-mono">Admin@123</span>)
          </div>
        </div>
        )}
      </div>

      {/* Right */}
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img src={HERO} alt="Construction site" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/70 via-zinc-900/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[10px] uppercase tracking-[0.3em] text-kabir mb-4 font-semibold">Built for construction</div>
          <h2 className="font-display text-5xl font-bold leading-tight mb-4 max-w-lg">
            From indent to payment,<br /><span className="text-kabir">flawlessly.</span>
          </h2>
          <p className="text-white/80 text-sm max-w-md leading-relaxed">
            Maker-checker workflow. RBAC. Three-way matching. Audit trail on every keystroke.
            Enterprise procurement for the modern construction company.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
            {[["11", "Roles"], ["9", "Modules"], ["100%", "Audited"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-bold text-kabir">{n}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/60">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
